import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataSelectEditDataPreviewComponent from 'webapp/components/data-select/edit/data-preview';
import DataSelectEditFormComponent from 'webapp/components/data-select/edit/form';
import DataSelectEditTabsComponent from 'webapp/components/data-select/edit/tabs';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type DataSelect from 'webapp/models-custom/data-select';

export default class DataSelectEditPopupComponent extends PopupInnerModalComponent<PopupData.DataSelect.Edit> {
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked selectedTab: DataSelect;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.DESIGNER_ADVANCED_POPUP, {
			source: this.popupData.sourceForTracking,
			origin: this.blockSetup?.block.trackingOrigin,
		});

		this.selectedTab = this.dataSelect;
	}

	get blockSetup() {
		return this.popupData.blockSetup;
	}

	get dataSelect() {
		return this.popupData.dataSelect;
	}

	@cached
	get model(): DataSelect[] {
		const dataSelects: DataSelect[] = [];
		if (this.dataSelect) {
			dataSelects.push(this.dataSelect);
			if (this.blockSetup) {
				const secondaryDataSelects =
					this.blockSetup.visualization.getSortedSecondaryDataSelectsForDataSelect(
						this.dataSelect,
					);
				dataSelects.push(...secondaryDataSelects);
			}
		}
		return dataSelects;
	}

	get isSingleTab() {
		return this.model.length <= 1;
	}

	@action
	changeTab(newTab: DataSelect): void {
		this.selectedTab = newTab;
	}
	<template>
		<div class="title">
			<span class="popup-title-icon">
				<CommonUiIconComponent @name="Settings" @size="sm" />
				Advanced metric settings for
				<EditableModelPropertyComponent
					@model={{this.selectedTab}}
					@editableProperty="shownTitle"
					@canEdit={{true}}
					@skipBESave={{true}}
				/>
			</span>
		</div>

		{{#unless this.isSingleTab}}
			<DataSelectEditTabsComponent
				@model={{this.model}}
				@selectedTab={{this.selectedTab}}
				@changeTab={{this.changeTab}}
				@popup={{@model}}
				@blockSetup={{this.blockSetup}}
			/>
		{{/unless}}

		<div class="content">

			<DataSelectEditFormComponent
				@popup={{@model}}
				@model={{this.selectedTab}}
				@closeOnSave={{this.isSingleTab}}
				@closeFn={{this.close}}
				@saveFn={{this.popupData.saveFn}}
				@blockSetup={{this.blockSetup}}
				@isScriptAdvanced={{this.popupData.isScriptAdvanced}}
			/>

			<DataSelectEditDataPreviewComponent
				@model={{this.selectedTab}}
				@onContentChange={{this.reposition}}
			/>

		</div>
	</template>
}
